import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useIsRedesignEnabled } from "@src/appV2/redesign/hooks/useIsRedesignEnabled";
import { Platform } from "@src/mobile/utils/platform";
import { createContext, type ReactNode, useContext, useLayoutEffect } from "react";

export const RebrandContext = createContext(false);

/**
 * Returns whether the rebrand is active.
 * Use this for any rebrand-conditional logic: logos, images, component variants, theme selection.
 */
export function useIsRebrand(): boolean {
  return useContext(RebrandContext);
}

/**
 * Reads the WORKPLACE_APP_REBRAND_ENABLED feature flag and provides
 * the result via context. Must be placed inside the LaunchDarkly provider
 * and above any component that calls useIsRebrand().
 */
export function RebrandProvider(props: { children: ReactNode }) {
  const isRedesignEnabled = useIsRedesignEnabled();

  const isRebrandEnabled = useCbhFlag(CbhFeatureFlag.WORKPLACE_APP_REBRAND_ENABLED, {
    defaultValue: false,
  });

  const isRebrand = isRedesignEnabled && (Platform.isMobile || isRebrandEnabled);

  useLayoutEffect(() => {
    if (isRebrand) {
      document.body.setAttribute("data-rebrand", "");
    } else {
      document.body.removeAttribute("data-rebrand");
    }
    return () => {
      document.body.removeAttribute("data-rebrand");
    };
  }, [isRebrand]);

  return <RebrandContext.Provider value={isRebrand}>{props.children}</RebrandContext.Provider>;
}
